import {
  Typography,
  Box,
  Divider,
  Grid,
  Alert,
  Button,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { fetchDatabaseRecords } from "../store/thunks/mdsThunks";
import { useEffect, useState } from "react";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import Loader from "../components/common/custom-component/Loader";
import { logger } from "helpers/logger";

export default function AboutUsPage() {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const [features, setFeatures] = useState([]);
  const [isLoadingBackdrop, setIsLoadingBackdrop] = useState(true);
  const [error, setError] = useState(null);
  const [retryTrigger, setRetryTrigger] = useState(0);

  useEffect(() => {
    let promiseRef = null;

    const fetchData = async () => {
      // Reset states when retrying
      setError(null);
      setIsLoadingBackdrop(true);

      promiseRef = dispatch(
        fetchDatabaseRecords({
          tableName: "features",
        })
      );

      const recordResult = await promiseRef;

      if (fetchDatabaseRecords.fulfilled.match(recordResult)) {
        const rec = recordResult?.payload?.data?.records;
        // Sort by order field
        const sortedFeatures = [...rec].sort((a, b) =>
          parseInt(a.order) - parseInt(b.order)
        );
        setFeatures(sortedFeatures);
        setIsLoadingBackdrop(false);
      } else if (fetchDatabaseRecords.rejected.match(recordResult)) {
        // Check if it's not a cancellation
        if (recordResult.payload !== 'cancelled') {
          setError(recordResult.payload || 'An error occurred while loading data');
        }
        setIsLoadingBackdrop(false);
      }
    };

    fetchData();

    // Cleanup: abort on unmount - just like saga!
    return () => {
      if (promiseRef) {
        logger.info('🛑 Component unmounting, aborting request');
        promiseRef.abort();
      }
    };
  }, [dispatch, retryTrigger]);

  const handleRetry = () => {
    setRetryTrigger(prev => prev + 1);
  };

  if (error) {
    return (
      <Box sx={{
        p: 4,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2
      }}>
        <Alert
          severity="error"
          sx={{ width: '100%', maxWidth: 600 }}
        >
          {error}
        </Alert>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={handleRetry}
        >
          Retry
        </Button>
      </Box>
    );
  }
  /*   if (isLoadingBackdrop) {
      return (
        <LoadingBackdrop 
          open={isLoadingBackdrop} 
          message="Loading data, please wait..." 
          variant="bars" 
          color="greenGradient" 
        />
      );
    } */
  if (features.length === 0) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <Loader
          variant="circular"
          message="Loading your data..."
          size="small"
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, mx: 'auto' }}>
      {/* Header */}
      <Typography variant="h3" gutterBottom color="primary">
        About Us Page
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph sx={{ whiteSpace: "pre-line" }}>
        {t("about_us_text")}
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* Features Section */}
      <Grid container spacing={4} sx={{ mt: 2 }}>
        {features.map((feature) => (
          <Grid item xs={12} sm={6} md={3} key={feature.id}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: 2,
              }}
            >
              {/* Icon */}
              <Box
                component="img"
                src={feature.image}
                alt={feature.title}
                sx={{
                  width: 80,
                  height: 80,
                  objectFit: "contain",
                }}
              />

              {/* Title */}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  minHeight: "48px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {feature.title}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}